import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box, Heading, VStack, Input, Select, Button, Text, Flex, Progress, useToast, Checkbox, CheckboxGroup,
    SimpleGrid, Icon, Alert, AlertIcon, AlertTitle, AlertDescription, Stack, FormControl, FormLabel, FormErrorMessage
} from '@chakra-ui/react';
import { FaFileCsv, FaCheckCircle, FaExclamationTriangle, FaDownload } from 'react-icons/fa';
import { startListaCompraExport, getExportStatus, downloadExportFile, getConfiguracoesListaCompras } from "../services/api";

const STATUS_LISTA = [
    { value: 'pendente', label: 'Pendente' },
    { value: 'em_andamento', label: 'Em andamento' },
    { value: 'respondido', label: 'Respondido' },
    { value: 'aprovado', label: 'Aprovado' },
    { value: 'finalizado', label: 'Finalizado' },
    { value: 'retirado', label: 'Retirado' },
    { value: 'cancelado', label: 'Cancelado' },
];
const ALL_STATUS = STATUS_LISTA.map(s => s.value);

const MAX_DIAS_PERIODO = 366;

// Data local em AAAA-MM-DD (toISOString usa UTC e vira o dia antes das 21h).
const dataLocal = (d) => {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};

const periodoPadrao = () => {
    const hoje = new Date();
    return { data_inicio: dataLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1)), data_fim: dataLocal(hoje) };
};

const validarPeriodo = ({ data_inicio, data_fim }) => {
    if (!data_inicio || !data_fim) return 'Informe a data inicial e a final.';
    if (data_fim < data_inicio) return 'A data final deve ser igual ou posterior à inicial.';
    const dias = (new Date(`${data_fim}T00:00:00`) - new Date(`${data_inicio}T00:00:00`)) / 86400000;
    if (dias > MAX_DIAS_PERIODO) return `O período máximo é de ${MAX_DIAS_PERIODO} dias.`;
    return null;
};

const primeiroErro = (error) => {
    const errors = error?.response?.data?.errors;
    if (errors) {
        const primeiro = Object.values(errors).flat()[0];
        if (primeiro) return primeiro;
    }
    if (error?.response?.status === 403) return 'Você não tem acesso a esta exportação.';
    return error?.response?.data?.message || 'Ocorreu um erro inesperado.';
};

const ExportarListasCompras = () => {
    const [filters, setFilters] = useState(() => ({ ...periodoPadrao(), fornecedor_id: '', status: ALL_STATUS }));
    const [fornecedores, setFornecedores] = useState([]);
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const toast = useToast();
    const intervalRef = useRef(null);

    const erroPeriodo = validarPeriodo(filters);

    useEffect(() => {
        let ativo = true;
        getConfiguracoesListaCompras().then(cfg => {
            if (!ativo) return;
            const lista = Array.isArray(cfg?.fornecedores) ? cfg.fornecedores : [];
            setFornecedores([...lista].sort((a, b) => String(a.nome).localeCompare(String(b.nome), 'pt-BR')));
        });
        return () => { ativo = false; };
    }, []);

    const pararPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleStatusChange = (status) => setFilters(prev => ({ ...prev, status }));

    const handleStartExport = async () => {
        if (erroPeriodo) {
            toast({ title: 'Período inválido', description: erroPeriodo, status: 'warning', duration: 5000, isClosable: true });
            return;
        }
        if (filters.status.length === 0) {
            toast({ title: 'Nenhum status selecionado', description: 'Selecione ao menos um status da lista.', status: 'warning', duration: 5000, isClosable: true });
            return;
        }

        pararPolling();
        setIsGenerating(true);
        setJobStatus(null);
        setJobId(null);

        try {
            const data = await startListaCompraExport({
                data_inicio: filters.data_inicio,
                data_fim: filters.data_fim,
                fornecedor_id: filters.fornecedor_id ? Number(filters.fornecedor_id) : null,
                status: filters.status.length === ALL_STATUS.length ? [] : filters.status,
            });
            setJobId(data.job_id);
            setJobStatus({ status: 'pending', progress: 0, message: 'Na fila de exportação...' });
        } catch (error) {
            toast({ title: 'Erro ao iniciar a exportação', description: primeiroErro(error), status: 'error', duration: 9000, isClosable: true });
            setIsGenerating(false);
        }
    };

    const fetchStatus = useCallback(async () => {
        if (!jobId) return;

        try {
            const statusData = await getExportStatus(jobId);
            setJobStatus(statusData);

            if (['completed', 'failed'].includes(statusData.status)) {
                setIsGenerating(false);
                pararPolling();
            }
        } catch (error) {
            console.error("Erro ao buscar status:", error);
            setJobStatus({ status: 'failed', message: 'Não foi possível acompanhar a exportação. Tente gerar de novo.' });
            setIsGenerating(false);
            pararPolling();
        }
    }, [jobId, pararPolling]);

    useEffect(() => {
        if (isGenerating && jobId && !intervalRef.current) {
            intervalRef.current = setInterval(fetchStatus, 3000);
        }
        return pararPolling;
    }, [isGenerating, jobId, fetchStatus, pararPolling]);

    const handleDownload = async () => {
        if (!jobStatus?.file_name) return;
        setIsDownloading(true);
        try {
            await downloadExportFile(jobStatus.file_name);
            toast({ title: 'Download iniciado', status: 'success', duration: 3000, isClosable: true });
        } catch (error) {
            toast({ title: 'Erro no download', description: 'Não foi possível baixar o arquivo. Gere a exportação de novo.', status: 'error', duration: 5000, isClosable: true });
        } finally {
            // O servidor apaga o arquivo depois de enviar: o link vale uma vez.
            setIsDownloading(false);
            setJobStatus(null);
            setJobId(null);
        }
    };

    const renderStatus = () => {
        if (!jobStatus) return null;
        switch (jobStatus.status) {
            case 'pending':
            case 'running':
                return (
                    <Box w="100%">
                        <Flex justify="space-between" mb={1} gap={2}>
                            <Text fontSize="sm" fontWeight="medium">{jobStatus.message}</Text>
                            <Text fontSize="sm" fontWeight="bold" color="orange.500">{jobStatus.progress || 0}%</Text>
                        </Flex>
                        <Progress value={jobStatus.progress || 0} size="md" colorScheme="orange" borderRadius="md" isIndeterminate={jobStatus.status === 'pending'} />
                    </Box>
                );
            case 'completed':
                return (
                    <Alert status="success" borderRadius="md" variant="left-accent" alignItems="flex-start">
                        <AlertIcon as={FaCheckCircle} />
                        <Box flex="1">
                            <AlertTitle>Arquivo pronto!</AlertTitle>
                            <AlertDescription display="block" fontSize="sm" mb={3}>{jobStatus.message}</AlertDescription>
                            <Button leftIcon={<FaDownload />} colorScheme="green" onClick={handleDownload} isLoading={isDownloading} loadingText="Baixando..." size="sm" w={{ base: 'full', sm: 'auto' }}>
                                Baixar Planilha
                            </Button>
                        </Box>
                    </Alert>
                );
            case 'failed':
                return (
                    <Alert status="error" borderRadius="md" variant="left-accent">
                        <AlertIcon as={FaExclamationTriangle} />
                        <Box flex="1">
                            <AlertTitle>Falha na exportação</AlertTitle>
                            <AlertDescription fontSize="sm">{jobStatus.message || 'Ocorreu um erro inesperado.'}</AlertDescription>
                        </Box>
                    </Alert>
                );
            default: return null;
        }
    };

    return (
        <Box ml={{ base: 0, lg: '60px' }} p={{ base: 4, md: 8 }} bg="gray.50" minH="100vh">
            <VStack spacing={{ base: 4, md: 6 }} align="stretch" maxW="5xl" mx="auto">
                <Box textAlign={{ base: 'center', md: 'left' }} px={{ base: 10, md: 0 }}>
                    <Heading color="#F68633" size="lg">Exportar Listas de Compras</Heading>
                    <Text fontSize="sm" color="gray.600" mt={1}>
                        Uma linha por item da lista: venda, fornecedor, o que foi pedido, o que o fornecedor respondeu e o tempo de cada etapa.
                    </Text>
                </Box>

                <Box p={{ base: 4, md: 6 }} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                    <Heading size="md" mb={4}>1. Filtros</Heading>
                    <Stack spacing={5}>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                            <FormControl isInvalid={!!erroPeriodo}>
                                <FormLabel fontSize="sm" mb={1}>Data inicial da lista</FormLabel>
                                <Input type="date" name="data_inicio" value={filters.data_inicio} onChange={handleFilterChange} max={filters.data_fim || undefined} />
                            </FormControl>
                            <FormControl isInvalid={!!erroPeriodo}>
                                <FormLabel fontSize="sm" mb={1}>Data final da lista</FormLabel>
                                <Input type="date" name="data_fim" value={filters.data_fim} onChange={handleFilterChange} min={filters.data_inicio || undefined} />
                                {erroPeriodo && <FormErrorMessage>{erroPeriodo}</FormErrorMessage>}
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm" mb={1}>Fornecedor</FormLabel>
                                <Select name="fornecedor_id" value={filters.fornecedor_id} onChange={handleFilterChange}>
                                    <option value="">Todos os fornecedores</option>
                                    {fornecedores.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                                </Select>
                            </FormControl>
                        </SimpleGrid>

                        <Box>
                            <Heading size="sm" mb={3}>Status da lista</Heading>
                            <Checkbox
                                colorScheme="orange"
                                isChecked={filters.status.length === ALL_STATUS.length}
                                isIndeterminate={filters.status.length > 0 && filters.status.length < ALL_STATUS.length}
                                onChange={(e) => handleStatusChange(e.target.checked ? ALL_STATUS : [])}
                                mb={3}
                                fontWeight="bold"
                            >
                                Todos os status
                            </Checkbox>
                            <CheckboxGroup colorScheme="orange" value={filters.status} onChange={handleStatusChange}>
                                <SimpleGrid columns={{ base: 2, md: 4, lg: 7 }} spacing={3}>
                                    {STATUS_LISTA.map(s => <Checkbox key={s.value} value={s.value}>{s.label}</Checkbox>)}
                                </SimpleGrid>
                            </CheckboxGroup>
                        </Box>
                    </Stack>
                </Box>

                <Box p={{ base: 4, md: 6 }} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                    <Heading size="md" mb={4}>2. Gere o arquivo</Heading>
                    <VStack spacing={4}>
                        <Button
                            leftIcon={<Icon as={FaFileCsv} />}
                            colorScheme="orange"
                            onClick={handleStartExport}
                            isLoading={isGenerating}
                            loadingText="Gerando..."
                            isDisabled={isGenerating || !!erroPeriodo || filters.status.length === 0}
                            w="full"
                            size="lg"
                        >
                            Gerar Planilha
                        </Button>
                        {renderStatus()}
                        <Text fontSize="xs" color="gray.500" w="100%">
                            Arquivo CSV (abre no Excel). O período considera a data da lista, com no máximo {MAX_DIAS_PERIODO} dias.
                            Os tempos são em horas: venda → lista, envio da lista → resposta do item e aprovação → retirada.
                        </Text>
                    </VStack>
                </Box>
            </VStack>
        </Box>
    );
};

export default ExportarListasCompras;
